/* eslint-disable no-nested-ternary */
import classNames from 'classnames';
import { fmtMoney, fmtPercent } from '@upstart/harmony/lib/formatter';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import {
  FinanceResponse,
  MinimalRate,
} from '@upstart/harmony/lib/legos/financingTypes';
import { isNumber } from 'lodash';
import React from 'react';
import { MdWarning } from 'react-icons/md';
import './UpstartFinanceDetails.scss';
import Tooltip from 'components/Tooltip';
import { Icon } from 'semantic-ui-react';

type FinanceDetailsTableRowProps = {
  rate: MinimalRate;
  selected: boolean;
  isOverLimit: boolean;
  isUnderLimit: boolean;
  isBackendOverLimit: boolean;
  displayBackendAmounts?: boolean;
};
function FinanceDetailsTableRow({
  rate,
  selected,
  isOverLimit,
  isUnderLimit,
  isBackendOverLimit,
  displayBackendAmounts,
}: FinanceDetailsTableRowProps) {
  const noOffer = !rate.apr;
  const outsideLimit = isOverLimit || isUnderLimit;
  const error = outsideLimit || noOffer || isBackendOverLimit;

  return (
    <tr className={classNames({ selected, rateError: error })}>
      <td>
        <div
          className={classNames('flex items-center justify-start', {
            selected,
            errorField: outsideLimit,
          })}
        >
          {selected && error && outsideLimit && <MdWarning />}
          {fmtMoney(rate.minFinanceAmount, 2)} -{' '}
          {fmtMoney(rate.maxFinanceAmount, 2)}
        </div>
      </td>
      {displayBackendAmounts && (
        <td>
          <div
            className={classNames('flex items-center justify-center', {
              selected,
              errorField: isBackendOverLimit,
            })}
          >
            {isNumber(rate.apr) ? (
              rate.maxBackendAmount ? (
                fmtMoney(rate.maxBackendAmount, 2)
              ) : (
                <span>
                  <Tooltip
                    width={300}
                    text="Offers within this finance limit have no maximum backend limit. As long as the combined front end, and back end amounts are below the total finance amount upper limit, the offer is valid."
                  >
                    <Icon name="info circle" />
                  </Tooltip>
                  <span>Flexible Amount</span>
                </span>
              )
            ) : (
              <>
                <MdWarning className="UpstartFinanceDetails-error" />
                No Rate
              </>
            )}
          </div>
        </td>
      )}
      <td>
        <div
          className={classNames('flex items-center justify-end', {
            selected,
          })}
        >
          {isNumber(rate.apr) ? fmtPercent(rate.apr) : ''}
        </div>
      </td>
    </tr>
  );
}

export interface IUpstartFinanceDetailsProps {
  financeResponse: FinanceResponse;
  financeAmountRates: MinimalRate[];
  highlightedRate?: MinimalRate;
  dealer?: DealerWithFlags;
}

export function UpstartFinanceDetails({
  financeResponse,
  financeAmountRates,
  highlightedRate,
  dealer,
}: IUpstartFinanceDetailsProps) {
  const {
    isInvalid,
    isFinanceAmountOverLimit,
    isFinanceAmountUnderLimit,
    isFinanceAmountInvalid,
    isBackendOverLimit,
  } = financeResponse.validations ?? {};

  const { financeAmount = 0, dealerReserve, dealerFeePercentage } =
    financeResponse.data ?? {};

  const dealerFlat =
    isNumber(financeAmount) && isNumber(dealerReserve)
      ? financeAmount * dealerReserve
      : null;

  const dealerFee = dealer?.flags?.showDealerFee
    ? financeAmount * (dealerFeePercentage ?? 0)
    : null;

  return (
    <div className="UpstartFinanceDetails">
      <div className="offer-section-title">Current Finance Details</div>
      <div>
        <div className="flex flex-wrap">
          <span className="flex flex-col pt-10 pr-25">
            <span className="offer-label pr-10">Finance Amount</span>
            <span
              className={classNames({
                'UpstartFinanceDetails-error': isFinanceAmountInvalid,
              })}
            >
              {fmtMoney(financeAmount, 2)}
            </span>
            {isInvalid && (
              <span className="UpstartFinanceDetails-error small">
                {!highlightedRate?.apr
                  ? 'No offer available'
                  : isFinanceAmountOverLimit
                  ? 'Value exceeds limits'
                  : isFinanceAmountUnderLimit
                  ? 'Value under limits'
                  : null}
              </span>
            )}
          </span>
          {dealer?.flags?.displayBackendAmounts &&
            isNumber(financeResponse.calculatedBackendAmount) && (
              <span className="flex flex-col pt-10 pr-25">
                <span className="offer-label pr-10">Backend Amount</span>
                <span
                  className={classNames({
                    'UpstartFinanceDetails-error': isBackendOverLimit,
                  })}
                >
                  {fmtMoney(financeResponse.calculatedBackendAmount ?? 0, 2)}
                </span>
                {isBackendOverLimit && (
                  <span className="UpstartFinanceDetails-error small">
                    Value exceeds limits
                  </span>
                )}
              </span>
            )}
          {isNumber(dealerFlat) && (
            <span className="flex flex-col pt-10 pr-25">
              <span className="offer-label pr-10">Dealer Flat</span>
              <span>{fmtMoney(dealerFlat, 2)}</span>
            </span>
          )}
          {dealer?.flags?.showDealerFee && isNumber(dealerFee) && (
            <span className="flex flex-col pt-10 pr-25">
              <span className="offer-label pr-10">Dealer Fee</span>
              <span>{fmtMoney(dealerFee, 2)}</span>
            </span>
          )}
        </div>
      </div>

      <div className="pt-15 offer-section-title">Preview Finance Details</div>
      <div>
        <table
          className="UpstartFinanceDetails-financeDetailsTable"
          cellSpacing="0"
        >
          <thead>
            <tr>
              <th>Max Finance Amount</th>
              {dealer?.flags?.displayBackendAmounts && <th>Max Backend</th>}
              <th>APR</th>
            </tr>
          </thead>
          <tbody>
            {financeAmountRates.map(rate => (
              <FinanceDetailsTableRow
                rate={rate}
                selected={highlightedRate?.rateId === rate.rateId}
                isOverLimit={!!isFinanceAmountOverLimit}
                isUnderLimit={!!isFinanceAmountUnderLimit}
                isBackendOverLimit={
                  !!(dealer?.flags?.displayBackendAmounts && isBackendOverLimit)
                }
                displayBackendAmounts={!!dealer?.flags?.displayBackendAmounts}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
